/**
 * NA-5172 — antd 24-column spans for the Approval search form.
 *
 * `lg` / `xl` / `xxl` are antd `Col` props. In this app the shared
 * `ConfigProvider` (`@moneyforward/ap-frontend-components`) sets antd's
 * breakpoints to lg ≥ 1200 px, xl ≥ 1600 px, xxl ≥ 1920 px, so they match the
 * design-system names used in the ticket.
 *
 * Field order is the form's DOM order and follows the Figma frame (file
 * rAQAStrNYwfNH5K54DMBOu, node 2939:42641); the antd Row wraps whenever the
 * next span would exceed 24, which yields 4 rows (lg) / 3 rows (xl) / 2 rows
 * (xxl) in every visibility case.
 */
export type Span = { lg: number; xl: number; xxl: number };

export const FIELD_ORDER = [
  'query',
  'number',
  'status',
  'ap_payee_id',
  'total_amount',
  'office_member_ids',
  'dept_ids',
  'report_dept_ids',
  'mf_file',
  'created_date',
  'submitted_date',
  'approved_date',
  'book_date',
  'due_date',
  'without_agent',
] as const;

export type FieldKey = (typeof FIELD_ORDER)[number];

export type SearchGridVisibility = {
  /** 申請者の所属部門 (`dept_ids`) is rendered */
  showDept: boolean;
  /** 申請時に選択した所属部門 (`report_dept_ids`) is rendered */
  showReportDept: boolean;
};

const span = (lg: number, xl: number, xxl: number): Span => ({ lg, xl, xxl });

/** 代理承認除外 checkbox — last cell in every case */
const CHECKBOX = span(2, 2, 2);

/** Case A — both department fields shown (15 cells). lg 4 rows · xl 3 rows · xxl 2 rows */
const BOTH_DEPTS: Record<FieldKey, Span> = {
  query: span(6, 5, 3),
  number: span(6, 5, 3),
  status: span(6, 5, 3),
  ap_payee_id: span(6, 4, 3),
  total_amount: span(6, 5, 3),
  office_member_ids: span(6, 5, 3),
  dept_ids: span(6, 5, 3),
  report_dept_ids: span(6, 5, 3),
  mf_file: span(6, 4, 3),
  created_date: span(6, 5, 3),
  submitted_date: span(6, 5, 3),
  approved_date: span(6, 5, 3),
  book_date: span(6, 5, 3),
  due_date: span(6, 5, 3),
  without_agent: CHECKBOX,
};

/**
 * Case B / C — exactly one department field shown (14 cells). The visible
 * field takes the hidden one's space on its row at lg (12) and xl (10); at xxl
 * the row still has room, so nothing widens.
 */
const SINGLE_DEPT = span(12, 10, 3);

/**
 * Case D — no department field shown (13 cells). The two cells that sat next
 * to the hidden fields widen at xl (office 10, mf_file 9); xxl row 1 =
 * 4,3,3,4,4,3,3 and row 2 = 5 × 4 + checkbox (1).
 */
const NO_DEPTS: Record<FieldKey, Span> = {
  query: span(6, 5, 4),
  number: span(6, 5, 3),
  status: span(6, 5, 3),
  ap_payee_id: span(6, 4, 4),
  total_amount: span(6, 5, 4),
  office_member_ids: span(6, 10, 3),
  dept_ids: SINGLE_DEPT, // not rendered in this case
  report_dept_ids: SINGLE_DEPT, // not rendered in this case
  mf_file: span(6, 9, 3),
  created_date: span(6, 5, 4),
  submitted_date: span(6, 5, 4),
  approved_date: span(6, 5, 4),
  book_date: span(6, 5, 4),
  due_date: span(6, 5, 4),
  without_agent: span(6, 2, 1),
};

export const getSearchGridSpans = ({
  showDept,
  showReportDept,
}: SearchGridVisibility): Record<FieldKey, Span> => {
  if (showDept && showReportDept) return BOTH_DEPTS;
  if (!showDept && !showReportDept) return NO_DEPTS;
  // exactly one department field: Case A spans, the visible one widened
  return {
    ...BOTH_DEPTS,
    dept_ids: showDept ? SINGLE_DEPT : BOTH_DEPTS.dept_ids,
    report_dept_ids: showReportDept ? SINGLE_DEPT : BOTH_DEPTS.report_dept_ids,
  };
};
